import { useLoaderData } from '@remix-run/react';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    pxToRem,
    ReadMore,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import React, { useRef } from 'react';

import * as S from './Description.style';
import type Product from '../../type/product';

interface DescriptionProps {
    data: { Content: Product['Content'] };
    componentAfter?: JSX.Element | null;
}

const Description = ({ data, componentAfter }: DescriptionProps) => {
    const { product } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy(
        {
            professionalID: product.Professional.ID,
            professionalTitle: product.Professional.Title,
            itemType: 'Product',
            link: product.Professional.Link,
        },
        {
            attachUTM: true,
            processRelativeURL: true,
        }
    );

    const { mobile } = useAppDisplayModeContext();

    const layoutRef = useRef<HTMLDivElement>(null);
    const { css } = useStyles();

    if (!mobile) {
        return (
            <div
                className={css(
                    S.AboutTextContent,
                    S.DescriptionDesktopContainer
                )}
                onClick={clickProxy}
            >
                <div className={css(S.ReadMoreContent)}>
                    <span style={{ display: 'inline-block', width: '100%' }}>
                        <span
                            className={css(S.ReadMoreBlurb)}
                            dangerouslySetInnerHTML={{
                                __html: data.Content ?? '',
                            }}
                            ref={layoutRef}
                        />
                        {componentAfter}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <ReadMore
            maxLines={3}
            content={{
                className: css(S.ReadMoreContent),
                onClick: clickProxy,
                children: (
                    <span style={{ display: 'inline-block', width: '100%' }}>
                        <span
                            className={css(S.ReadMoreBlurb)}
                            dangerouslySetInnerHTML={{
                                __html: data.Content ?? '',
                            }}
                            ref={layoutRef}
                        />
                        {componentAfter}
                    </span>
                ),
            }}
            readMoreText={'View more'}
            readLessText={'View less'}
            className={css(S.AboutTextContent)}
            variables={{
                elementGap: 0,
                toggleFontSize: pxToRem(26),
                linkColor: 'inherit',
                contentLineHeight: 1.5,
            }}
            scrollOffset={150}
        />
    );
};

export default Description;
